import React from 'react';
import { motion } from 'framer-motion';
import { Flame } from 'lucide-react';
import { Star, Squiggle } from './MemphisShapes';

const burgers = [
  {
    id: "classic",
    name: "CLASSIC",
    price: 15.00,
    description: "Doppio smash di manzo, cheddar, lattuga, cipolla caramellata, cetriolini e salsa burger.",
    allergens: "1, 3, 7, 10, 11"
  },
  {
    id: "bacon",
    name: "BACON",
    price: 15.00,
    description: "Doppio smash di manzo, cheddar, bacon, salsa BBQ e cipolla caramellata.",
    allergens: "1, 3, 7, 10, 11",
    spicy: "Disponibile anche SPICY, con jalapeños e salsa piccante: +1,00 €"
  },
  {
    id: "mediterraneo",
    name: "MEDITERRANEO",
    price: 15.00,
    description: "Doppio smash di manzo, burrata di bufala, pomodori semi-dry e pesto al basilico.",
    allergens: "1, 7, 8, 11"
  },
  {
    id: "chicken",
    name: "CHICKEN",
    price: 15.00,
    description: "Chicken Burger croccante da 200 g, lattuga, pomodoro, scaglie di grana e salsa Caesar.",
    allergens: "1, 3, 4, 7, 10, 11"
  }
];

const BurgerCard = ({ burger, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    whileHover={{ y: -6, rotate: index % 2 === 0 ? -0.5 : 0.5 }}
    className="relative bg-white border-4 border-black rounded-2xl p-6 md:p-8 shadow-brutal-yellow transition-shadow duration-200 hover:shadow-brutal-lg"
    data-testid={`burger-item-${burger.id}`}
  >
    {/* Big outlined index number */}
    <span className="absolute -top-6 -left-2 font-fredoka font-bold text-6xl md:text-7xl text-santomato-yellow select-none" style={{ WebkitTextStroke: '2px #000' }}>
      0{index + 1}
    </span>

    <div className="flex justify-between items-start gap-4 mt-4">
      <h3 className="font-fredoka font-bold text-2xl md:text-3xl text-black uppercase">
        {burger.name}
      </h3>
      <span className="bg-black text-santomato-yellow font-fredoka font-bold text-xl md:text-2xl px-3 py-1 rounded-full border-2 border-black whitespace-nowrap">
        €{burger.price.toFixed(2)}
      </span>
    </div>

    <p className="font-nunito text-sm md:text-base text-gray-700 mt-3 leading-relaxed">
      {burger.description}
    </p>

    {burger.spicy && (
      <div
        className="mt-4 inline-flex items-center gap-2 bg-santomato-red text-white border-2 border-black rounded-full px-4 py-1.5"
        data-testid="burger-spicy-tag"
      >
        <Flame size={16} strokeWidth={2.5} />
        <span className="font-nunito font-bold text-xs md:text-sm">{burger.spicy}</span>
      </div>
    )}

    <p className="font-nunito text-xs text-gray-500 mt-4" data-testid={`burger-allergens-${burger.id}`}>
      Allergeni: {burger.allergens}
    </p>
  </motion.div>
);

const Burger = () => {
  return (
    <section
      id="burger"
      data-testid="burger-section"
      className="min-h-screen bg-black py-20 md:py-28 relative overflow-hidden"
    >
      {/* Decorative elements */}
      <Star className="absolute top-16 right-8 opacity-40" color="#FFD600" size={55} />
      <Star className="absolute bottom-24 left-10 opacity-40" color="#FF2A2A" size={40} />
      <Squiggle className="absolute top-1/2 right-4 opacity-30 hidden lg:block" color="#FFD600" width={90} />

      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, y: -40, rotate: -8 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", bounce: 0.5, duration: 0.8 }}
            className="mb-2"
          >
            <motion.img
              src="https://customer-assets-lxgj4vgw.emergentagent.net/job_santomato-pizza/artifacts/e7998c22ce81ce76_Pomodoro%20Mascotte%20in%20Azione%20con%20Burger.png"
              alt="Mascotte Santomato con smash burger"
              data-testid="burger-mascot"
              loading="lazy"
              className="w-48 md:w-64 mx-auto"
              animate={{ y: [0, -12, 0], rotate: [0, 2, -2, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
          <h2
            className="font-fredoka font-bold text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.95]"
            data-testid="burger-title"
          >
            <span className="text-santomato-yellow">SMASH</span>
            <br />
            <span className="text-outline-white">BURGER</span>
          </h2>
          <p className="font-nunito font-bold text-base md:text-lg text-gray-300 max-w-2xl mx-auto mt-5">
            Schiacciato sulla piastra rovente: croccante fuori, succoso dentro.
          </p>
        </motion.div>

        {/* Burger grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {burgers.map((burger, index) => (
            <BurgerCard key={burger.id} burger={burger} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Burger;
